import React, { useState } from 'react';
import { ChatSection } from '../chat/ChatSection';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { useUserHistory } from '../../hooks/useUserHistory';
import { useChatInput } from '../../contexts/ChatContext';
import { SearchPagination } from '../ui/SearchPagination';

const ITEMS_PER_PAGE = 5;

export const HistorySearchPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { searchHistory } = useUserHistory();
  const { setInputValue } = useChatInput();
  const query = (location.state as { query?: string })?.query || '';
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentSearches = searchHistory.slice(startIndex, endIndex);

  const toggleExpand = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSearchClick = (query: string, searchId: string) => {
    setInputValue(query);
    
    // Scroll to chat section
    const chatSection = document.querySelector('[data-chat-section]');
    if (chatSection) {
      setTimeout(() => {
        chatSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setExpandedItems(new Set());
  };

  const isTextTruncated = (text: string): boolean => {
    const words = text.split(' ');
    const estimatedChars = text.length;
    return estimatedChars > 50 || words.length > 12;
  };

  const renderSearchHistoryList = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md transition-colors">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Your Search History
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          View and reuse your previous search queries
        </p>
      </div>
      
      <div className="p-6">
        {searchHistory.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400">
            No search history yet
          </p>
        ) : (
          <>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {currentSearches.map((item) => (
                <div
                  key={item.id}
                  className="block py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group cursor-pointer"
                  onClick={() => handleSearchClick(item.query, item.id)}
                >
                  <div>
                    <p className={`text-sm text-gray-700 dark:text-gray-200 group-hover:text-gray-900 dark:group-hover:text-white ${
                      expandedItems.has(item.id) ? '' : 'line-clamp-2'
                    }`}>
                      {item.query}
                    </p>
                    {isTextTruncated(item.query) && (
                      <div
                        onClick={(e) => toggleExpand(e, item.id)}
                        className="mt-1 text-blue-600 dark:text-blue-400 text-sm flex items-center gap-1 cursor-pointer hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        {expandedItems.has(item.id) ? (
                          <>
                            Show less
                            <ChevronUp className="w-4 h-4" />
                          </>
                        ) : (
                          <>
                            Show more
                            <ChevronDown className="w-4 h-4" />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <span>{item.searches.toLocaleString()} searches</span>
                    <span className="text-gray-300 dark:text-gray-600">•</span>
                    <span>
                      {new Date(item.lastUsedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <SearchPagination
              currentPage={currentPage}
              totalItems={searchHistory.length}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-0 sm:px-4 py-8">
      <div className="mb-6 px-4 sm:px-0">
        <button
          onClick={() => navigate('/search-history')}
          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Search History
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ChatSection 
            initialQuery={query}
            autoSearch={false}
          />
          
          <div className="block lg:hidden mt-6">
            {renderSearchHistoryList()}
          </div>
        </div>
        
        <div className="lg:col-span-1 hidden lg:block">
          <div className="sticky top-8">
            {renderSearchHistoryList()}
          </div>
        </div>
      </div>
    </div>
  );
};